import { useCallback, useEffect, useMemo, useState } from 'react';
import { useModal } from '../context/ModalContext';
import { adminService } from '../services/adminService';
import api from '../services/api';
import { HK_TIME_ZONE } from '../utils/dateUtils';

const SUBMISSION_TABS = {
  internal: 'internal',
  external: 'external'
};

const styles = {
  page: {
    minHeight: '100%',
    background: '#f5f7fb',
    padding: '32px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    padding: '20px'
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    borderBottom: '1px solid #d9e0ea',
    marginBottom: '20px'
  },
  tabs: {
    display: 'flex',
    gap: '24px',
    marginBottom: '-1px'
  },
  tab: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: '#7a8699',
    fontSize: '15px',
    fontWeight: 600,
    padding: '0 0 14px',
    cursor: 'pointer',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent'
  },
  tabActive: {
    color: '#273947',
    borderBottomColor: '#273947'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '16px'
  },
  toolbarGroup: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  input: {
    border: '1px solid #d9e0ea',
    borderRadius: '6px',
    padding: '10px 12px',
    fontSize: '14px',
    color: '#1f2937',
    background: '#ffffff',
    outline: 'none'
  },
  select: {
    minWidth: '240px'
  },
  search: {
    minWidth: '220px'
  },
  outlineButton: {
    appearance: 'none',
    border: '1px solid #d9e0ea',
    background: '#ffffff',
    color: '#475569',
    borderRadius: '6px',
    padding: '10px 14px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  countText: {
    color: '#7a8699',
    fontSize: '14px',
    marginBottom: '12px'
  },
  tableWrap: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    borderBottom: '1px solid #d9e0ea',
    color: '#64748b',
    fontSize: '13px',
    fontWeight: 700
  },
  td: {
    padding: '14px 16px',
    borderBottom: '1px solid #eef2f7',
    color: '#1f2937',
    fontSize: '14px',
    verticalAlign: 'middle'
  },
  subtle: {
    color: '#64748b'
  },
  actions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap'
  },
  primarySmButton: {
    appearance: 'none',
    border: '1px solid #273947',
    background: '#273947',
    color: '#ffffff',
    borderRadius: '4px',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  secondarySmButton: {
    appearance: 'none',
    border: '1px solid #d9e0ea',
    background: '#ffffff',
    color: '#475569',
    borderRadius: '4px',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer'
  },
  dangerSmButton: {
    appearance: 'none',
    border: '1px solid #f1c6cb',
    background: '#fff5f5',
    color: '#dc3545',
    borderRadius: '4px',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 16px',
    color: '#7a8699'
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px 16px',
    color: '#7a8699'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    zIndex: 1200
  },
  modal: {
    width: '90%',
    maxWidth: '1200px',
    height: '90%',
    background: '#ffffff',
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb'
  },
  modalTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 700,
    color: '#1f2937'
  },
  closeButton: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: '#64748b',
    fontSize: '24px',
    cursor: 'pointer'
  },
  modalBody: {
    flex: 1
  },
  previewFrame: {
    width: '100%',
    height: '100%',
    border: 'none'
  }
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  // Hong Kong time, matching ClinicSubmissionsPage — the browser's own timezone
  // would show a different day to a clinic user abroad.
  return date.toLocaleString('en-CA', {
    timeZone: HK_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function getDisplayPatient(submission) {
  return submission.patientName || submission.recipientEmail || '—';
}

export default function SubmissionsPage() {
  const { showAlert, showConfirm } = useModal();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [formFilter, setFormFilter] = useState('all');
  const [activeTab, setActiveTab] = useState(SUBMISSION_TABS.internal);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getAllSubmissions();
      setSubmissions(Array.isArray(data?.submissions) ? data.submissions : []);
    } catch (error) {
      console.error('Failed to load submissions:', error);
      await showAlert('Failed to load submissions', 'Error');
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const formOptions = useMemo(() => {
    const counts = {};
    submissions.forEach((item) => {
      const key = item.formName || 'Unknown';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, count]) => ({ name, count }));
  }, [submissions]);

  const filteredByTab = useMemo(() => {
    return submissions.filter((item) => {
      const isInternal = item.isInternal ?? item.formType === 'internal' ?? Boolean(item.clinicName || item.clinicCode || item.clinicId);
      return activeTab === SUBMISSION_TABS.internal ? isInternal : !isInternal;
    });
  }, [activeTab, submissions]);

  const visibleSubmissions = useMemo(() => {
    return filteredByTab.filter((item) => {
      const matchesForm = formFilter === 'all' || (item.formName || 'Unknown') === formFilter;
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch = !term || [
        item.filename,
        item.formName,
        item.patientName,
        item.recipientEmail,
        item.clinicName,
        item.clinicCode
      ].some(value => (value || '').toLowerCase().includes(term));
      return matchesForm && matchesSearch;
    });
  }, [filteredByTab, formFilter, searchTerm]);

  const handlePreview = async (formId, filename) => {
    try {
      const response = await api.get(`/forms/${formId}/submissions/${encodeURIComponent(filename)}`, {
        params: { preview: true },
        responseType: 'blob'
      });
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const nextUrl = URL.createObjectURL(blob);
      setPreviewUrl(nextUrl);
      setPreviewFilename(filename);
    } catch (error) {
      console.error('Preview failed:', error);
      await showAlert('Failed to preview PDF', 'Error');
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewFilename('');
  };

  const handleDownload = async (formId, filename) => {
    try {
      const response = await api.get(`/forms/${formId}/submissions/${encodeURIComponent(filename)}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      await showAlert('Failed to download PDF', 'Error');
    }
  };

  const handleDelete = async (formId, filename) => {
    const confirmed = await showConfirm(`Are you sure to permanently delete ${filename}? You will not be able to recover this submission.`, {
      title: 'Delete Submission',
      okText: 'Delete',
      danger: true
    });

    if (!confirmed) return;

    try {
      await adminService.deleteSubmission(formId, filename);
      await loadSubmissions();
    } catch (error) {
      console.error('Delete failed:', error);
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to delete submission';
      await showAlert(message, 'Error');
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.topBar}>
          <div style={styles.tabs}>
            <button
              type="button"
              style={{ ...styles.tab, ...(activeTab === SUBMISSION_TABS.internal ? styles.tabActive : null) }}
              onClick={() => setActiveTab(SUBMISSION_TABS.internal)}
            >
              Internal (Clinic)
            </button>
            <button
              type="button"
              style={{ ...styles.tab, ...(activeTab === SUBMISSION_TABS.external ? styles.tabActive : null) }}
              onClick={() => setActiveTab(SUBMISSION_TABS.external)}
            >
              External (Link)
            </button>
          </div>
        </div>

        <div style={styles.toolbar}>
          <div style={styles.toolbarGroup}>
            <select
              style={{ ...styles.input, ...styles.select }}
              value={formFilter}
              onChange={(event) => setFormFilter(event.target.value)}
            >
              <option value="all">All Forms</option>
              {formOptions.map((option) => (
                <option key={option.name} value={option.name}>
                  {option.name} ({option.count})
                </option>
              ))}
            </select>

            <input
              type="text"
              style={{ ...styles.input, ...styles.search }}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search submissions..."
            />
          </div>

          <button type="button" style={styles.outlineButton} onClick={loadSubmissions}>
            Refresh
          </button>
        </div>

        <div style={styles.countText}>{visibleSubmissions.length} submissions</div>

        {loading ? (
          <div style={styles.loadingState}>Loading submissions...</div>
        ) : visibleSubmissions.length === 0 ? (
          <div style={styles.emptyState}>No submissions found.</div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>REF ID</th>
                  <th style={styles.th}>FILE NAME</th>
                  <th style={styles.th}>TEMPLATE NAME</th>
                  <th style={styles.th}>DATE</th>
                  <th style={styles.th}>CLINIC</th>
                  <th style={styles.th}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {visibleSubmissions.map((submission, index) => (
                  <tr key={`${submission.formId}-${submission.filename}-${index}`}>
                    <td style={{ ...styles.td, ...styles.subtle }}>
                      {submission.referenceId || '—'}
                    </td>
                    <td style={styles.td}>{submission.filename || getDisplayPatient(submission)}</td>
                    <td style={styles.td}>{submission.formName || 'Unknown'}</td>
                    <td style={{ ...styles.td, ...styles.subtle }}>
                      {formatDate(submission.uploadedAt || submission.createdAt)}
                    </td>
                    <td style={styles.td}>{submission.clinicCode || submission.clinicName || '-'}</td>
                    <td style={{ ...styles.td, minWidth: '220px' }}>
                      <div style={styles.actions}>
                        <button
                          type="button"
                          style={styles.primarySmButton}
                          onClick={() => handlePreview(submission.formId, submission.filename)}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          style={styles.secondarySmButton}
                          onClick={() => handleDownload(submission.formId, submission.filename)}
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          style={styles.dangerSmButton}
                          onClick={() => handleDelete(submission.formId, submission.filename)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {previewUrl && (
        <div style={styles.modalOverlay} onClick={closePreview}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{previewFilename}</h3>
              <button type="button" style={styles.closeButton} onClick={closePreview}>
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <iframe title={previewFilename} src={previewUrl} style={styles.previewFrame} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
